import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FilePdf, CaretRight } from '@phosphor-icons/react';

/**
 * Sprint C — ReportStatusCard
 * Last upload date + CTA. Source: /api/health/state.report_status.
 * { documents_count: int, last_upload_at: ISO|null }
 */
function _formatRelative(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? '' : 's'} ago`;
    if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? '' : 's'} ago`;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

export const ReportStatusCard = ({ status }) => {
  const navigate = useNavigate();
  const documents = status?.documents_count ?? 0;
  const lastRel = _formatRelative(status?.last_upload_at);

  let primary;
  let subtitle;
  let ctaLabel;
  if (documents === 0) {
    primary = 'No reports yet';
    subtitle = 'Add a recent test or letter — every upload sharpens Kindra.';
    ctaLabel = 'Upload your first';
  } else if (!lastRel || lastRel === null) {
    primary = `${documents} report${documents === 1 ? '' : 's'} on file`;
    subtitle = 'Add a recent result if you have one.';
    ctaLabel = 'Open reports';
  } else {
    primary = `Last upload ${lastRel}`;
    subtitle = `${documents} report${documents === 1 ? '' : 's'} in your timeline.`;
    ctaLabel = 'Open reports';
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/documents')}
      data-testid="report-status-card"
      className="w-full text-left bg-white border border-[#E5DFD0] rounded-2xl p-5 hover:border-slate-300 transition-colors flex items-center gap-4 group"
    >
      <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0">
        <FilePdf size={18} weight="duotone" className="text-[#0E1C2E]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1">
          Reports
        </p>
        <p className="text-[14.5px] font-medium text-[#0E1C2E] leading-snug">{primary}</p>
        <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">{subtitle}</p>
      </div>
      <div className="flex items-center gap-1 text-[#235347] flex-shrink-0">
        <span className="text-[12px] font-medium hidden sm:inline">{ctaLabel}</span>
        <CaretRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
};

export default ReportStatusCard;
